'use client';

import { useState } from 'react';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  Monitor,
  PhoneOff,
  MessageSquare,
  Users,
} from 'lucide-react';
import { cn } from '../../lib/utils';

export interface VideoRoomProps {
  sessionId: string;
  participantName: string;
  onEndCall?: () => void;
}

export function VideoRoom({ sessionId: _sessionId, participantName, onEndCall }: VideoRoomProps) {
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const participantInitials = participantName
    .split(' ')
    .map((n) => n[0])
    .join('');
  const controlBaseClass =
    'flex h-12 w-12 items-center justify-center rounded-full transition-interactive duration-200';

  return (
    <div className="flex h-screen flex-col bg-surface-inverse">
      {/* Video area */}
      <div className="relative flex-1">
        {/* Main video (remote participant) */}
        <div className="flex h-full items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-statura-green-accessible text-3xl font-semibold text-white">
              {participantInitials}
            </div>
            <p className="font-heading text-card-title text-white">{participantName}</p>
            <p className="font-body text-body-sm text-slate-400">Connecting...</p>
          </div>
        </div>

        {/* Self video (picture-in-picture) */}
        <div className="absolute bottom-4 right-4 h-32 w-44 overflow-hidden rounded-xl border border-white/10 bg-slate-800 shadow-xl">
          <div className="flex h-full items-center justify-center">
            {isCameraOff ? (
              <VideoOff className="h-8 w-8 text-slate-500" />
            ) : (
              <p className="font-body text-caption text-slate-400">Your camera</p>
            )}
          </div>
        </div>
      </div>

      {/* Controls bar */}
      <div className="flex items-center justify-center gap-4 border-t border-white/10 bg-surface-inverse p-6">
        <button
          onClick={() => setIsMuted(!isMuted)}
          className={cn(
            controlBaseClass,
            isMuted ? 'bg-red-500 text-white' : 'bg-white/10 text-white hover:bg-white/20',
          )}
        >
          {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
        </button>
        <button
          onClick={() => setIsCameraOff(!isCameraOff)}
          className={cn(
            controlBaseClass,
            isCameraOff ? 'bg-red-500 text-white' : 'bg-white/10 text-white hover:bg-white/20',
          )}
        >
          {isCameraOff ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
        </button>
        <button
          onClick={() => setIsScreenSharing(!isScreenSharing)}
          className={cn(
            controlBaseClass,
            isScreenSharing
              ? 'bg-statura-green-accessible text-white'
              : 'bg-white/10 text-white hover:bg-white/20',
          )}
        >
          <Monitor className="h-5 w-5" />
        </button>
        <button className={cn(controlBaseClass, 'bg-white/10 text-white hover:bg-white/20')}>
          <MessageSquare className="h-5 w-5" />
        </button>
        <button className={cn(controlBaseClass, 'bg-white/10 text-white hover:bg-white/20')}>
          <Users className="h-5 w-5" />
        </button>
        <button
          onClick={onEndCall}
          className={cn(controlBaseClass, 'bg-red-600 text-white hover:bg-red-700')}
        >
          <PhoneOff className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
